// Web Speech API wrappers.
//
// Two surfaces:
//   1. Text-to-Speech (TTS) - reads a question aloud. Used at the start of each question.
//      Built on `window.speechSynthesis`. Available in every modern browser and on iOS Safari.
//   2. Speech-to-Text (STT) - captures the child's spoken answer. Built on
//      `webkitSpeechRecognition` / `SpeechRecognition`. Available in Chrome (desktop and Android),
//      Safari on iOS 14.5+, and Edge. Where it is not available we fall back to manual typing
//      and surface a small note in the UI.
//
// Privacy: Web Speech API runs through the browser vendor's speech servers (Apple, Google, etc.).
// The audio leaves the device for transcription. This is documented in the privacy policy.
// No audio is ever sent to OUR servers - we only receive the transcribed text from the browser.

type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
};

interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string; confidence: number }>>;
  resultIndex: number;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function isSpeechRecognitionAvailable(): boolean {
  return getSpeechRecognitionCtor() !== null;
}

export function isSpeechSynthesisAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

// ----- Text to Speech -----

interface SpeakOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
  /** Preferred BCP-47 language tag (e.g. "en-GB"). */
  lang?: string;
}

let cachedVoices: SpeechSynthesisVoice[] = [];

function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (!isSpeechSynthesisAvailable()) {
      resolve([]);
      return;
    }
    const existing = window.speechSynthesis.getVoices();
    if (existing.length > 0) {
      cachedVoices = existing;
      resolve(existing);
      return;
    }
    // Some browsers (especially Chrome) load voices asynchronously.
    const handler = () => {
      cachedVoices = window.speechSynthesis.getVoices();
      window.speechSynthesis.removeEventListener('voiceschanged', handler);
      resolve(cachedVoices);
    };
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    // Safety timeout: some browsers never fire voiceschanged.
    setTimeout(() => {
      if (cachedVoices.length === 0) {
        cachedVoices = window.speechSynthesis.getVoices();
        resolve(cachedVoices);
      }
    }, 600);
  });
}

// Voice names that are typically female across Apple, Google, and Microsoft TTS.
// We prefer these over the default male voices (Daniel on en-GB, Alex on en-US, etc.)
// because Sarah's real-world testing showed the default voice felt too mechanical.
const FEMALE_VOICE_HINTS = /Samantha|Karen|Catherine|Tessa|Kate|Moira|Susan|Allison|Ava|Serena|Fiona|Veena|Joana|Mariska|Yelda|Yuna|Linda|Heather|Zoe|Tessa|Carmit|Lekha|Hortense|Marie|Audrey|Amelie|Emily|Female/i;

function pickVoice(lang: string, voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | undefined {
  if (voices.length === 0) return undefined;
  const family = lang.split('-')[0];
  const candidates = voices.filter((v) => v.lang.startsWith(family));

  // First try: same exact lang AND female-sounding name.
  const exactFemale = candidates.find((v) => v.lang === lang && FEMALE_VOICE_HINTS.test(v.name));
  if (exactFemale) return exactFemale;

  // Second try: same language family, female-sounding name (e.g. Samantha en-US for en-GB request).
  const anyFemale = candidates.find((v) => FEMALE_VOICE_HINTS.test(v.name));
  if (anyFemale) return anyFemale;

  // Third try: same exact lang (any gender).
  const exact = candidates.find((v) => v.lang === lang);
  if (exact) return exact;

  // Last fallback: any voice in the language family.
  return candidates[0];
}

/** Speak the given text. Returns a promise that resolves when speech finishes. */
export async function speak(text: string, opts: SpeakOptions = {}): Promise<void> {
  if (!isSpeechSynthesisAvailable()) return;
  await loadVoices();

  return new Promise((resolve) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = opts.rate ?? 0.92; // Slightly slower than default - friendlier for dyslexic users.
    utterance.pitch = opts.pitch ?? 1;
    utterance.volume = opts.volume ?? 1;
    utterance.lang = opts.lang ?? 'en-GB';

    const voice = pickVoice(utterance.lang, cachedVoices);
    if (voice) utterance.voice = voice;

    utterance.onend = () => resolve();
    utterance.onerror = () => resolve(); // Resolve on error too so the UI does not hang.

    // Some Safari builds need a tick of breathing room.
    window.speechSynthesis.cancel();
    setTimeout(() => window.speechSynthesis.speak(utterance), 50);
  });
}

export function cancelSpeech() {
  if (!isSpeechSynthesisAvailable()) return;
  window.speechSynthesis.cancel();
}

export function pauseSpeech() {
  if (!isSpeechSynthesisAvailable()) return;
  window.speechSynthesis.pause();
}

export function resumeSpeech() {
  if (!isSpeechSynthesisAvailable()) return;
  window.speechSynthesis.resume();
}

export function isSpeechSpeaking(): boolean {
  if (!isSpeechSynthesisAvailable()) return false;
  return window.speechSynthesis.speaking && !window.speechSynthesis.paused;
}

export function isSpeechPaused(): boolean {
  if (!isSpeechSynthesisAvailable()) return false;
  return window.speechSynthesis.paused;
}

// Read-aloud preference. Stored as the disabled-state so a missing key means default-on.
const STORAGE_KEY_READ_DISABLED = 'mdt_read_aloud_disabled';

export function isReadAloudEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_READ_DISABLED) !== 'true';
  } catch {
    return true;
  }
}

export function setReadAloudEnabled(enabled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY_READ_DISABLED, String(!enabled));
  } catch {
    // Storage unavailable - the preference will fall back to default.
  }
}

// ----- Speech to Text -----

export interface DictationHandle {
  stop: () => void;
  abort: () => void;
}

export interface DictationCallbacks {
  /** Called continuously with the best-guess transcript so far. */
  onPartial?: (transcript: string) => void;
  /** Called once when the user stops speaking. */
  onFinal: (transcript: string) => void;
  /** Called on error or when not supported. */
  onError?: (message: string) => void;
}

/**
 * Start listening for a single dictation. The session ends when the user stops speaking
 * for a beat, or when the caller invokes `stop()`. Returns a handle for control.
 */
export function startDictation(callbacks: DictationCallbacks, lang = 'en-GB'): DictationHandle {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) {
    callbacks.onError?.('Speech recognition is not available on this device. Type your answer instead.');
    return { stop: () => {}, abort: () => {} };
  }

  const recognition = new Ctor();
  recognition.lang = lang;
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
  recognition.continuous = false;

  let finalTranscript = '';

  recognition.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i] as ArrayLike<{ transcript: string }> & { isFinal?: boolean };
      const text = result[0]?.transcript ?? '';
      if ((result as { isFinal?: boolean }).isFinal) {
        finalTranscript += text;
      } else {
        interim += text;
      }
    }
    callbacks.onPartial?.((finalTranscript + interim).trim());
  };

  recognition.onerror = (event) => {
    callbacks.onError?.(humaniseError(event.error));
  };

  recognition.onend = () => {
    callbacks.onFinal(finalTranscript.trim());
  };

  try {
    recognition.start();
  } catch (e) {
    callbacks.onError?.((e as Error).message || 'Could not start microphone.');
  }

  return {
    stop: () => {
      try {
        recognition.stop();
      } catch {
        // Already stopped.
      }
    },
    abort: () => {
      try {
        recognition.abort();
      } catch {
        // Already stopped.
      }
    },
  };
}

function humaniseError(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission denied. Allow microphone access in your settings to use dictation.';
    case 'no-speech':
      return 'I did not hear anything. Try again, or type your answer.';
    case 'audio-capture':
      return 'I could not access the microphone. Try again.';
    case 'network':
      return 'Speech recognition needs an internet connection.';
    default:
      return `Speech recognition error (${code}). Try again, or type your answer.`;
  }
}
